import { useCallback } from "react";
import usePostalCode, { getPostalCode } from "../../../hook/usePostalCode";
import useValueList, { getValueLabel } from "../../../hook/useValueList";
import { IAddress } from "../../../types/address";

/**
 *
 * Refer to <a href="https://www.canadapost-postescanada.ca/cpc/en/support/articles/addressing-guidelines/canadian-addresses.page">Addressing guidelines</a>
 */

export default function useAddressFormatter(): [
  formatAddress: (address?: IAddress, separator?: string) => string // separator is required for full address
] {
  const [streetTypes] = useValueList('STREET_TYPE_CODE');
  const [streetDirections] = useValueList('STREET_DIRECT_CODE');
  const [installationTypes] = useValueList('INSTALLTION_TYPE_CODE');
  const [ruralServices] = useValueList('ROUTE_SERVICE_TYPE_CODE');

  const [cities] = useValueList('CITY');
  const [provinces] = useValueList('PROVINCE');
  const [countries] = useValueList('COUNTRY');

  const [postalCodes] = usePostalCode();

  const getPostalBox = (address: IAddress) => {
    if (!address.poBoxNumber) return '';

    return [
      'PO Box',
      address.poBoxNumber,
      getValueLabel(installationTypes, address.installationTypeCodeId),
      address.installationName
    ]
      .filter(item => item)
      .join(' ');
  }

  const getCivicAddress = useCallback((address: IAddress) => (
    [
      address.civicNumber,
      address.civicNumberSuffix,
      address.streetName,
      getValueLabel(streetTypes, address.streetTypeCodeId),
      getValueLabel(streetDirections, address.streetDirCodeId)
    ]
      .filter(item => item)
      .join(' ')
  ), [streetTypes, streetDirections]);

  const getRuralRoute = useCallback((address: IAddress) => (
    [
      getValueLabel(ruralServices, address.routeServiceTypeCodeId),
      address.routeServiceNumber
    ]
      .filter(item => item)
      .join(' ')
  ), [ruralServices]);

  const getLocality = useCallback((address: IAddress) => (
    [
      getValueLabel(cities, address.municipalityId),
      getValueLabel(provinces, address.provinceId),
      getPostalCode(postalCodes, address.postalCodeId)
    ]
      .filter(item => item)
      .join(' ')
  ), [cities, provinces, countries, postalCodes]);

  const formatAddress = (address?: IAddress, separator?: string) => {
    if (!address) {
      return '';
    } else if (separator) {
      const country = getValueLabel(countries, address.countryId);
      return [
        address.buildingName,
        getCivicAddress(address),
        getPostalBox(address),
        getRuralRoute(address),
        getLocality(address),
        country?.toUpperCase() !== 'CANADA' && country
      ]
        .filter(item => item)
        .join(separator);
    } else {
      return [
        getCivicAddress(address),
        getPostalBox(address),
        getRuralRoute(address)
      ]
        .filter(item => item)
        .join(', ');
    }
  };

  return [formatAddress];
}
